import React, { useEffect, useRef, useState } from "react";
import { Fingerprint, LogOut, ScanFace } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBiometric } from "@/hooks/useBiometric";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { isIOS } from "@/lib/native";

export const LOCK_KEY = "mm369_applock_after";
const HIDDEN_AT = "mm369_applock_hidden_at";
export const LOCK_OPTIONS = [
  { v: "0", label: "Every time I reopen the app" },
  { v: "300", label: "After 5 minutes away (recommended)" },
  { v: "1800", label: "After 30 minutes away" },
  { v: "off", label: "Off — never lock" },
];
export const lockSetting = () => localStorage.getItem(LOCK_KEY) || "300";

export function AppLock() {
  const { user, login, logout, setSession } = useAuth();
  const bio = useBiometric();
  const [locked, setLocked] = useState(false);
  const [pwd, setPwd] = useState("");
  const [usePwd, setUsePwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const autoTried = useRef(false);

  const enabled = !!user && bio.hint && lockSetting() !== "off";

  useEffect(() => {
    if (!enabled) { setLocked(false); return; }
    const shouldLock = () => {
      const at = Number(sessionStorage.getItem(HIDDEN_AT) || localStorage.getItem(HIDDEN_AT) || 0);
      const secs = Number(lockSetting());
      return !at || (Date.now() - at) / 1000 >= secs;
    };
    if (!sessionStorage.getItem("mm369_applock_booted")) { sessionStorage.setItem("mm369_applock_booted", "1"); if (shouldLock()) setLocked(true); }
    const onVis = () => {
      if (document.visibilityState === "hidden") { const t = String(Date.now()); sessionStorage.setItem(HIDDEN_AT, t); localStorage.setItem(HIDDEN_AT, t); }
      else if (shouldLock()) { setLocked(true); autoTried.current = false; }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("pagehide", onVis); };
  }, [enabled]);

  const unlockBio = async () => {
    const d = await bio.unlock(true);
    if (d) { setSession(d.user, d.token); setLocked(false); setUsePwd(false); setPwd(""); }
  };

  useEffect(() => {
    if (locked && !autoTried.current && bio.available) { autoTried.current = true; setTimeout(unlockBio, 400); }
  }, [locked, bio.available]); // eslint-disable-line

  useEffect(() => { document.body.classList.toggle("app-locked", locked); }, [locked]);

  const unlockPwd = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await login(user.email, pwd, true); setLocked(false); setUsePwd(false); setPwd(""); }
    catch (err) { toast.error("Wrong password", { description: err.response?.data?.detail || err.message }); }
    finally { setBusy(false); }
  };

  if (!locked || !user) return null;
  const Icon = isIOS() ? ScanFace : Fingerprint;
  return (
    <div data-testid="app-lock" className="fixed inset-0 z-[100] bg-zinc-950 text-white flex flex-col items-center justify-center p-6 safe-top safe-bottom">
      <img src="/icon-192.png" alt="" className="w-14 h-14 rounded-2xl mb-6 opacity-90" />
      <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Locked</div>
      <h1 className="font-heading font-black text-2xl mt-1 text-center" data-testid="app-lock-name">{user.name || user.email}</h1>
      <p className="text-xs text-zinc-400 mt-1">Your session is safe — verify it's you to continue.</p>

      {!usePwd ? (
        <>
          <button data-testid="app-lock-biometric-btn" onClick={unlockBio} disabled={bio.busy}
            className="mt-10 w-28 h-28 rounded-full bg-white text-zinc-900 grid place-items-center shadow-[0_0_0_12px_rgba(255,255,255,0.06)] active:scale-95 transition-transform disabled:opacity-60">
            <Icon size={52} strokeWidth={1.5} />
          </button>
          <div className="mt-4 text-sm font-semibold">{bio.busy ? "Verifying…" : isIOS() ? "Tap to unlock with Face ID" : "Tap to unlock with biometrics"}</div>
          <button data-testid="app-lock-use-password" onClick={() => setUsePwd(true)} className="mt-8 text-xs text-zinc-400 underline decoration-dotted hover:text-white">Use password instead</button>
        </>
      ) : (
        <form onSubmit={unlockPwd} className="mt-8 w-full max-w-xs space-y-3" data-testid="app-lock-password-form">
          <Input data-testid="app-lock-password-input" type="password" autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Password" className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500 h-11" />
          <button data-testid="app-lock-password-submit" disabled={busy || !pwd} className="w-full h-11 bg-white text-zinc-900 font-semibold text-sm disabled:opacity-60">{busy ? "Checking…" : "Unlock"}</button>
          <button type="button" onClick={() => setUsePwd(false)} className="w-full text-xs text-zinc-400 underline decoration-dotted">Back to {isIOS() ? "Face ID" : "biometrics"}</button>
        </form>
      )}

      <button data-testid="app-lock-signout" onClick={() => { setLocked(false); logout(); }} className="absolute bottom-8 text-xs text-zinc-500 flex items-center gap-1 hover:text-white safe-bottom"><LogOut size={12} /> Sign out</button>
    </div>
  );
}

export function AppLockSettings() {
  const bio = useBiometric();
  const [v, setV] = useState(lockSetting());
  const [, force] = useState(0);
  useEffect(() => { const h = () => force((n) => n + 1); window.addEventListener("mm369:webauthn-changed", h); return () => window.removeEventListener("mm369:webauthn-changed", h); }, []);
  if (!bio.hint) return null;
  const save = (val) => { setV(val); localStorage.setItem(LOCK_KEY, val); toast.success(val === "off" ? "App lock turned off on this device" : "App lock updated"); };
  return (
    <section className="bg-white border border-zinc-200 p-4 space-y-3" data-testid="applock-settings">
      <div className="flex items-center gap-2">
        <ScanFace size={16} />
        <h2 className="font-heading font-bold tracking-tight text-lg">App lock</h2>
        <span className="text-[10px] tracking-[0.14em] uppercase text-zinc-500 font-semibold ml-auto">this device</span>
      </div>
      <p className="text-xs text-zinc-500">Ask for {isIOS() ? "Face ID" : "fingerprint / Face ID"} when you come back to the app, so nobody else can open your books.</p>
      <div className="grid gap-1.5">
        {LOCK_OPTIONS.map((o) => (
          <button key={o.v} data-testid={`applock-opt-${o.v}`} onClick={() => save(o.v)}
            className={`text-left text-xs px-3 py-2 border transition-colors ${v === o.v ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}>{o.label}</button>
        ))}
      </div>
    </section>
  );
}
